import React from 'react';
import clsx from 'clsx';

export function Card(props: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={clsx(
        'overflow-hidden rounded-2xl border border-zinc-200 bg-white',
        'shadow-sm',
        props.className
      )}
    >
      {props.children}
    </div>
  );
}

export function CardHeader(props: { title: string; subtitle?: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-zinc-300 bg-zinc-200 px-4 py-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-zinc-900">{props.title}</div>
        {props.subtitle ? (
          <div className="mt-0.5 truncate text-xs text-zinc-600">{props.subtitle}</div>
        ) : null}
      </div>
      {props.right ? <div className="shrink-0">{props.right}</div> : null}
    </div>
  );
}

export function CardBody(props: { className?: string; children: React.ReactNode }) {
  return <div className={clsx('px-4 py-3', props.className)}>{props.children}</div>;
}

export function Button(props: {
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
  disabled?: boolean;
  className?: string;
}) {
  const variant = props.variant ?? 'primary';
  const base = 'inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm font-medium transition';
  const styles =
    variant === 'primary'
      ? 'bg-accent text-white hover:brightness-95 disabled:opacity-50'
      : variant === 'secondary'
      ? 'bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50'
      : variant === 'outline'
      ? 'border border-zinc-300 bg-white text-zinc-900 hover:bg-zinc-50'
      : 'bg-transparent text-zinc-900 hover:bg-zinc-100';

  return (
    <button
      type={props.type ?? 'button'}
      onClick={props.onClick}
      disabled={props.disabled}
      className={clsx(base, styles, props.className)}
    >
      {props.children}
    </button>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={clsx(
        'w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm',
        'focus:outline-none focus:ring-2 focus:ring-accent/30',
        props.className
      )}
    />
  );
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={clsx(
        'w-full rounded-xl border border-zinc-300 bg-white px-3 py-2 text-sm',
        'focus:outline-none focus:ring-2 focus:ring-accent/30',
        props.className
      )}
    />
  );
}

export function TagChip(props: { label: string; active?: boolean; selected?: boolean; onClick?: () => void }) {
  const active = props.active ?? props.selected ?? false;
  return (
    <button
      onClick={props.onClick}
      className={clsx(
        'rounded-full border px-3 py-1 text-xs font-medium transition',
        active
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50'
      )}
    >
      {props.label}
    </button>
  );
}

export function SectionTitle(props: { title: string; right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h1 className="text-lg font-semibold tracking-tight">{props.title}</h1>
      {props.right}
    </div>
  );
}
